import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bot, BarChart3, Crown, Users, Package, Play, ChevronRight, ChevronLeft, Shield, Brain, Zap, TrendingUp } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import ErrorBoundary from './ErrorBoundary';

// Import robot images
import larryWilliamsImg from '@/assets/quantmake-robo-trader-larry-williams-premium-day-trade.jpg';
import smartgridLightImg from '@/assets/quantmake-autotrade-smartgrid-light-robo-trader.jpg';
import fibonacciImg from '@/assets/quantmake-fibonacci-retracoes-robo-trader-tecnico.jpg';
import newSmartgridImg from '@/assets/quantmake-smartgrid-premium-robo-trader-automatizado.jpg';
import combo3xImg from '@/assets/quantmake-combo-3x-ultra-robos-traders-day-trade.jpg';
import combo2xImg from '@/assets/quantmake-combo-2x-ultra-autotrade-quanttrade.jpg';

const RobotsSection = memo(() => {
  const headerAnimation = useScrollAnimation({ threshold: 0.3 });
  const statsAnimation = useScrollAnimation({ threshold: 0.2 });
  const cardsAnimation = useScrollAnimation({ threshold: 0.1 });
  const [hoveredRobot, setHoveredRobot] = useState<number | null>(null);
  const [isHoveringSection, setIsHoveringSection] = useState(false);
  const [isAnyCardHovered, setIsAnyCardHovered] = useState<boolean>(false);
  const [isNavigationHovered, setIsNavigationHovered] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [expandedCardWidth, setExpandedCardWidth] = useState<string>('w-80 sm:w-96');

  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<NodeJS.Timeout>();
  const [scrollDirection, setScrollDirection] = useState<'right' | 'left'>('right');
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();

  // Mobile touch handling states
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMobileInteracting, setIsMobileInteracting] = useState(false);
  const touchDebounceRef = useRef<NodeJS.Timeout>();

  // Detect mobile/desktop device with debounce
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const isMobileScreen = window.innerWidth < 768;
        const isDesktopScreen = window.innerWidth >= 768;
        setIsMobile(isMobileScreen);
        setIsDesktop(isDesktopScreen);
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Get card width with fixed desktop width (memoized)
  const getCardWidth = useCallback((robotId: number, isExpanded: boolean) => {
    if (!isExpanded) return 'w-72 sm:w-80';

    // DESKTOP: ALWAYS fixed width
    if (isDesktop) {
      return 'w-96';
    }

    // MOBILE: calculate dynamically only when necessary
    return calculateMobileExpansionWidth();
  }, [isDesktop]);

  // Calculate optimal expansion width for MOBILE ONLY (memoized)
  const calculateMobileExpansionWidth = useCallback(() => {
    if (isDesktop) {
      return 'w-96'; // Silent fallback
    }

    if (!carouselRef.current) return 'w-72';

    const viewportWidth = window.innerWidth;
    const cardPosition = carouselRef.current.scrollLeft || 0;
    const spaceToRight = viewportWidth - cardPosition - 288;

    if (spaceToRight >= 384) return 'w-96';
    if (spaceToRight >= 320) return 'w-80';
    return 'w-72';
  }, [isDesktop]);

  // Auto-scroll optimized with reduced dependencies
  useEffect(() => {
    // Clear any existing interval
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = undefined;
    }

    // Don't start auto-scroll if paused conditions
    if (isHoveringSection || isMobileInteracting || hoveredRobot !== null || isDesktop) {
      return;
    }

    const autoScroll = () => {
      const carousel = carouselRef.current;
      if (!carousel) return;

      try {
        const scrollLeft = carousel.scrollLeft;
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        const scrollAmount = window.innerWidth < 768 ? 288 : 320;

        if (scrollDirection === 'right') {
          if (scrollLeft >= maxScroll - 100) {
            setScrollDirection('left');
          } else {
            carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          }
        } else {
          if (scrollLeft <= 100) {
            setScrollDirection('right');
          } else {
            carousel.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
          }
        }
      } catch (error) {
        // Silent error handling
        if (autoScrollRef.current) {
          clearInterval(autoScrollRef.current);
          autoScrollRef.current = undefined;
        }
      }
    };

    autoScrollRef.current = setInterval(autoScroll, 3000);

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
        autoScrollRef.current = undefined;
      }
    };
  }, [isHoveringSection, isMobileInteracting, hoveredRobot, scrollDirection, isDesktop]);

  // Navigation functions (memoized)
  const scrollPrev = useCallback(() => {
    if (!carouselRef.current) return;
    
    const cardWidth = window.innerWidth < 768 ? 288 : 320;
    carouselRef.current.scrollBy({ left: -cardWidth, behavior: 'smooth' });
  }, []);

  const scrollNext = useCallback(() => {
    if (!carouselRef.current) return;
    
    const cardWidth = window.innerWidth < 768 ? 288 : 320;
    carouselRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
  }, []);

  // Add carousel event listeners for mobile
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || !isMobile) return;

    const handleCarouselScroll = () => {
      // If scrolling the carousel, close expansion
      if (hoveredRobot !== null) {
        setHoveredRobot(null);
        setIsMobileInteracting(false);
        setIsAnyCardHovered(false);
      }
    };

    carousel.addEventListener('scroll', handleCarouselScroll, { passive: true });

    return () => {
      carousel.removeEventListener('scroll', handleCarouselScroll);
    };
  }, [isMobile, hoveredRobot]);

  // Inicialização simples
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0; // Começa no início
    }
  }, []);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (touchDebounceRef.current) clearTimeout(touchDebounceRef.current);
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    };
  }, []);

  // Memoized robots data to prevent re-renders
  const robots = useMemo(() => [
    {
      id: 1,
      name: "LarryWilliams 9.1",
      category: "PREMIUM",
      type: "individual",
      price: "R$ 1.497",
      monthlyPrice: "R$ 77",
      link: "https://rebrand.ly/LW_Premium",
      performance: "+189.3%",
      trades: "12.450",
      winRate: "91.7%",
      maxDrawdown: "2.8%",
      timeframe: "1-15 min",
      image: larryWilliamsImg,
      description: "Baseado no renomado setup de Larry Williams, este robô foi aprimorado para elevar a precisão das entradas e potencializar as oportunidades do mercado. Com ajustes refinados no ponto de entrada, oferece uma abordagem mais assertiva na abertura de posições, combinando a consagrada metodologia de Larry Williams com upgrades modernos.",
      features: ["Análise de padrões avançados", "IA adaptativa", "Stop loss inteligente", "Backtests 15+ anos"],
      availability: "Disponível isolado e em combos",
      gradient: "from-yellow-400 via-orange-500 to-red-600"
    },
    {
      id: 2,
      name: "SmartGrid Light",
      category: "ESSENCIAL",
      type: "combo-only",
      price: "Apenas em combos",
      monthlyPrice: "",
      link: "https://rebrand.ly/Combo2x",
      performance: "+87.4%",
      trades: "5.230",
      winRate: "78.9%",
      maxDrawdown: "4.1%",
      timeframe: "5-30 min",
      image: smartgridLightImg,
      description: "O Smart Grid redefine estratégias de gradiente linear ao identificar regiões de preço-chave para montar posições com inteligência e precisão. Sua tecnologia ajusta o preço médio da operação para condições favoráveis, reduzindo a exposição ao mercado. Projetado para traders que desejam eficiência, controle e alto desempenho.",
      features: ["Grid dinâmico", "Baixo risco", "Fácil configuração", "Ideal para iniciantes"],
      availability: "Exclusivo em combos",
      gradient: "from-blue-400 via-cyan-500 to-teal-600"
    },
    {
      id: 3,
      name: "Retração Fibonacci",
      category: "TÉCNICO",
      type: "combo-only",
      price: "Apenas em combos",
      monthlyPrice: "",
      link: "https://rebrand.ly/comboUltra3x",
      performance: "+124.6%",
      trades: "3.890",
      winRate: "84.2%",
      maxDrawdown: "3.5%",
      timeframe: "15-60 min",
      image: fibonacciImg,
      description: "Desbloqueie o poder da análise de Fibonacci com o Robô Retração Diária, que identifica zonas de preço justo com base na retração diária. Este robô mapeia os níveis mais estratégicos do mercado, oferecendo operações precisas e calculadas. Seja para captar reversões ou aproveitar a continuidade da tendência, o Retração Diária é o aliado perfeito para traders que desejam transformar retrações de preço em grandes oportunidades.",
      features: ["Níveis de Fibonacci", "Reversões precisas", "Análise matemática", "Alta precisão"],
      availability: "Exclusivo em combos",
      gradient: "from-purple-400 via-pink-500 to-rose-600"
    },
    {
      id: 4,
      name: "SmartGrid Premium",
      category: "PREMIUM",
      type: "individual",
      price: "R$ 997",
      monthlyPrice: "R$ 297",
      link: "https://rebrand.ly/c_SmartGridPremium",
      performance: "+156.8%",
      trades: "8.750",
      winRate: "86.4%",
      maxDrawdown: "3.9%",
      timeframe: "5-30 min",
      image: newSmartgridImg,
      description: "SMART GRID combina sistema de exaustão de preço com grade móvel dinâmica, permitindo entradas manuais enquanto gerencia operações complexas. Possui configuração rápida/lenta para diferentes volatilidades, alvo financeiro definível e grade não linear personalizável, oferecendo controle manual com automação inteligente para máxima flexibilidade.",
      features: ["IA Neural avançada", "Adaptação automática", "Multi-timeframe", "Grid inteligente"],
      availability: "Disponível isolado e em combos",
      gradient: "from-green-400 via-emerald-500 to-teal-600"
    },
    {
      id: 5,
      name: "Combo 3x Ultra",
      category: "COMBO PLUS",
      type: "combo",
      price: "R$ 1.997",
      monthlyPrice: "R$ 499,80",
      link: "https://rebrand.ly/comboUltra3x",
      performance: "+198.7%",
      trades: "24.570",
      winRate: "89.1%",
      maxDrawdown: "2.1%",
      timeframe: "Multi",
      image: combo3xImg,
      description: "Plano com Módulo de Automação de Estratégias Plus + 3 Robôs",
      planType: "Plus",
      robots: [
        {
          name: "Smart Grid - Gradiente Linear Light",
          description: "O Smart Grid redefine estratégias de gradiente linear ao identificar regiões de preço-chave para montar posições com inteligência e precisão. Sua tecnologia ajusta o preço médio da operação para condições favoráveis, reduzindo a exposição ao mercado enquanto busca deslocamentos significativos de preço. Seja para mercados voláteis ou consolidados, o Smart Grid é projetado para traders que desejam eficiência, controle e alto desempenho em suas operações."
        },
        {
          name: "Robô Larry Williams Premium",
          description: "Baseado no renomado setup de Larry Williams, este robô foi aprimorado para elevar a precisão das entradas e potencializar as oportunidades do mercado. Com ajustes refinados no ponto de entrada, o Larry Williams Premium oferece uma abordagem mais assertiva na abertura de posições, capturando movimentos estratégicos com maior eficiência. Ideal para traders que buscam um sistema confiável, este robô combina a consagrada metodologia de Larry Williams com upgrades modernos para resultados consistentes."
        },
        {
          name: "Retração Diária - Fibonacci",
          description: "Desbloqueie o poder da análise de Fibonacci com o Robô Retração Diária, que identifica zonas de preço justo com base na retração diária. Este robô mapeia os níveis mais estratégicos do mercado, oferecendo operações precisas e calculadas. Seja para captar reversões ou aproveitar a continuidade da tendência, o Retração Diária é o aliado perfeito para traders que desejam transformar retrações de preço em grandes oportunidades."
        }
      ],
      features: ["Módulo de Automação Plus", "3 Robôs premium inclusos", "Suporte técnico prioritário", "Atualizações vitalícias", "Comunidade VIP"],
      availability: "Pack exclusivo - Economia de 47%",
      includes: ["Smart Grid - Gradiente Linear Light", "Robô Larry Williams Premium", "Retração Diária - Fibonacci"],
      gradient: "from-indigo-400 via-purple-500 to-pink-600"
    },
    {
      id: 6,
      name: "Combo 2x Ultra",
      category: "COMBO BASIC",
      type: "combo",
      price: "R$ 1.497",
      monthlyPrice: "R$ 176,42",
      link: "https://rebrand.ly/Combo2x",
      performance: "+167.9%",
      trades: "17.680",
      winRate: "87.8%",
      maxDrawdown: "2.6%",
      timeframe: "Multi",
      image: combo2xImg,
      description: "Plano com Módulo de Automação de Estratégias Basic + 2 Robôs",
      planType: "Basic",
      robots: [
        {
          name: "Smart Grid - Gradiente Linear Light",
          description: "O Smart Grid redefine estratégias de gradiente linear ao identificar regiões de preço-chave para montar posições com inteligência e precisão."
        },
        {
          name: "Robô Larry Williams Premium",
          description: "Baseado no renomado setup de Larry Williams, este robô foi aprimorado para elevar a precisão das entradas e potencializar as oportunidades do mercado."
        }
      ],
      features: ["Módulo de Automação Basic", "2 Robôs premium inclusos", "Suporte técnico", "Atualizações vitalícias"],
      availability: "Pack exclusivo - Economia de 33%",
      includes: ["Smart Grid - Gradiente Linear Light", "Robô Larry Williams Premium"],
      gradient: "from-cyan-400 via-blue-500 to-indigo-600"
    }
  ], []);

  const getCategoryIcon = (category: string) => {
    if (category.includes('PREMIUM')) return <Crown className="h-4 w-4" />;
    if (category.includes('COMBO')) return <Package className="h-4 w-4" />;
    if (category === 'TÉCNICO') return <BarChart3 className="h-4 w-4" />;
    return <Bot className="h-4 w-4" />;
  };

  const getCategoryColor = (category: string) => {
    if (category.includes('PREMIUM')) return 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10';
    if (category.includes('COMBO')) return 'text-purple-400 border-purple-400/30 bg-purple-400/10';
    if (category === 'TÉCNICO') return 'text-cyan-400 border-cyan-400/30 bg-cyan-400/10';
    return 'text-green-400 border-green-400/30 bg-green-400/10';
  };

  // Desktop mouse handlers (memoized)
  const handleCardMouseEnter = useCallback((robotId: number) => {
    setIsAnyCardHovered(true);

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredRobot(robotId);

      const cardWidth = isDesktop ? 'w-96' : calculateMobileExpansionWidth();
      setExpandedCardWidth(cardWidth);

      // Disable scroll-snap during expansion
      if (carouselRef.current) {
        carouselRef.current.style.scrollSnapType = 'none';
      }
    }, isDesktop ? 50 : 100);
  }, [isDesktop, calculateMobileExpansionWidth]);

  const handleCardMouseLeave = useCallback(() => {
    setIsAnyCardHovered(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredRobot(null);

      // Re-enable scroll-snap after expansion
      if (carouselRef.current) {
        carouselRef.current.style.scrollSnapType = 'x mandatory';
      }
    }, 150);
  }, []);

  // Touch handlers for mobile
  const handleTouchStart = (event: any, robotId: number) => {
    if (isMobile) {
      setTouchStartX(event.touches[0].clientX);
      setTouchStartY(event.touches[0].clientY);
      setIsScrolling(false);

      // Only expand if not already expanded
      if (hoveredRobot !== robotId) {
        setIsAnyCardHovered(true);
        setHoveredRobot(robotId);
        setExpandedCardWidth(calculateMobileExpansionWidth());
        setIsMobileInteracting(true);

        // Disable scroll-snap during expansion
        if (carouselRef.current) {
          carouselRef.current.style.scrollSnapType = 'none';
        }
      }
    }
  };

  const handleTouchMove = (event: any) => {
    if (isMobile) {
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      const deltaX = Math.abs(touchX - touchStartX);
      const deltaY = Math.abs(touchY - touchStartY);

      // If horizontal movement > 10px, it's a scroll
      if (deltaX > 10 || deltaY > 10) {
        setIsScrolling(true);
      }
    }
  };

  const handleTouchEnd = () => {
    if (isMobile) {
      if (touchDebounceRef.current) {
        clearTimeout(touchDebounceRef.current);
      }

      touchDebounceRef.current = setTimeout(() => {
        if (isScrolling) {
          // If was scrolling, close expansion quickly
          setIsAnyCardHovered(false);
          setHoveredRobot(null);
          setIsMobileInteracting(false);

          // Re-enable scroll-snap
          if (carouselRef.current) {
            carouselRef.current.style.scrollSnapType = 'x mandatory';
          }
        } else if (hoveredRobot !== null) {
          // If was a tap, keep expanded and auto-close after 4 seconds
          setTimeout(() => {
            setIsAnyCardHovered(false);
            setHoveredRobot(null);
            setIsMobileInteracting(false);

            if (carouselRef.current) {
              carouselRef.current.style.scrollSnapType = 'x mandatory';
            }
          }, 4000);
        }
      }, 150);
    }
  };

  const handleNavigationMouseEnter = () => {
    setIsNavigationHovered(true);
  };

  const handleNavigationMouseLeave = () => {
    setIsNavigationHovered(false);
  };

  const getNavigationButtonClasses = (position: 'left' | 'right') => {
    const baseClasses = `absolute top-1/2 -translate-y-1/2 z-[100] w-10 h-20 transition-all duration-300 ease-out rounded-xl group pointer-events-auto items-center justify-center hidden md:flex`;

    const transparentClasses = `bg-transparent border-transparent text-transparent backdrop-blur-none shadow-none pointer-events-none`;

    const normalClasses = `bg-blue-400/40 backdrop-blur-md border-2 border-blue-400/60 text-blue-100 shadow-lg shadow-blue-400/40 hover:bg-green-400/40 hover:border-green-400/60 hover:text-white hover:shadow-lg hover:shadow-green-400/40 pointer-events-auto`;

    const positionClasses = position === 'left' ? '-left-5' : '-right-5';

    const shouldBeTransparent = isAnyCardHovered && !isNavigationHovered;

    return `${baseClasses} ${positionClasses} ${shouldBeTransparent ? transparentClasses : normalClasses}`;
  };

  const renderRobotCard = (robot: any, keyPrefix: string = '') => {
    const isExpanded = hoveredRobot === robot.id;
    const cardWidth = isExpanded
      ? (isDesktop ? 'w-96' : expandedCardWidth)
      : 'w-72 sm:w-80';

    return (
      <div
        key={`${keyPrefix}${robot.id}`}
        className={`flex-none transition-all duration-300 ease-out ${cardWidth}`}
        style={{
          height: window.innerWidth < 768 ? '420px' : '480px',
          scrollSnapAlign: hoveredRobot === robot.id ? 'none' : 'start',
          scrollSnapStop: hoveredRobot === robot.id ? 'normal' : 'always',
          transformOrigin: 'left center',
          willChange: 'transform, width',
          maxWidth: isMobile ? 'calc(100vw - 2rem)' : 'none'
        }}
      >
        <div
          className="group relative cursor-pointer w-full h-full"
          onMouseEnter={() => handleCardMouseEnter(robot.id)}
          onMouseLeave={handleCardMouseLeave}
          onTouchStart={(e) => handleTouchStart(e, robot.id)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'manipulation' }}
        >
        <Card className={`relative bg-slate-900/90 border-slate-700/50 backdrop-blur-sm overflow-hidden w-full h-full transition-all duration-300 ease-out ${hoveredRobot === robot.id ? 'shadow-2xl shadow-purple-500/20 border-purple-400/50 z-50' : 'hover:shadow-lg hover:shadow-slate-900/50'}`}>
          <div className={`flex w-full h-full transition-all duration-300 ease-out ${
            hoveredRobot === robot.id
              ? 'flex-col lg:flex-row'
              : 'flex-col'
          }`}>
            <div className={`relative overflow-hidden transition-all duration-300 ease-out ${
              hoveredRobot === robot.id
                ? 'w-full lg:w-2/5 h-2/5 lg:h-full'
                : 'w-full h-3/5 sm:h-3/5'
            }`}>
              <img
                src={robot.image}
                alt={`${robot.name} - Robô Trader Automatizado QuantMake.AI para Day Trade com ${robot.performance} performance comprovada - Sistema AutoTrade Premium Nelogica Profit`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                title={`${robot.name} QuantMake - Trading automatizado com IA e ${robot.winRate} taxa de acerto`}
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${robot.gradient} opacity-20`}></div>
              <Badge className={`absolute top-2 left-2 ${getCategoryColor(robot.category)} border text-xs font-medium`}>
                {getCategoryIcon(robot.category)}
                <span className="ml-1">{robot.category}</span>
              </Badge>
            </div>
            <div className={`flex flex-col transition-all duration-300 ease-out ${
              hoveredRobot === robot.id
                ? 'w-full lg:w-3/5 h-3/5 lg:h-full p-3 pb-2 lg:p-4'
                : 'w-full h-2/5 p-3 sm:p-4'
            }`}>
              <div className={`transition-all duration-300 ease-out ${hoveredRobot === robot.id ? 'mb-2 lg:mb-3' : 'mb-2'}`}>
                <h3 className={`font-bold text-white transition-all duration-300 ease-out ${
                  hoveredRobot === robot.id
                    ? 'text-base lg:text-lg mb-1 lg:mb-2'
                    : 'text-sm sm:text-base mb-1'
                }`}>
                  {robot.name}
                </h3>
                {hoveredRobot !== robot.id && (
                  <p className="text-slate-400 text-xs line-clamp-2">
                    {robot.description}
                  </p>
                )}
              </div>
              {hoveredRobot === robot.id && (
                <div className="flex-1 flex flex-col pb-1">
                  {robot.robots ? (
                    <div className="flex-1 mb-2 lg:mb-3 max-h-48 md:max-h-none overflow-hidden">
                      {robot.planType && (
                        <div className="mb-1 p-2 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded border border-purple-400/30">
                          <div className="text-purple-300 font-bold text-xs">Plano {robot.planType}</div>
                          <div className="text-slate-400 text-xs">Módulo de Automação de Estratégias</div>
                        </div>
                      )}
                      <div className="bg-slate-800/50 p-2 lg:p-3 rounded">
                        <div className="text-cyan-400 font-bold text-xs mb-1 lg:mb-2">Automações Incluídas:</div>
                        <div className="space-y-1">
                          {robot.robots.slice(0, window.innerWidth < 768 ? 3 : robot.robots.length).map((robotInfo: any, index: number) => (
                            <div key={index} className="text-white text-xs flex items-center">
                              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></div>
                              {robotInfo.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 mb-2 lg:mb-3">
                      <div className="bg-slate-800/50 p-1.5 lg:p-3 rounded">
                        <p className="text-slate-300 text-xs leading-normal description-truncate">
                          {robot.description}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex-shrink-0 mt-auto mb-2">
                    <Button
                      className="w-full bg-gradient-to-r from-green-400 to-cyan-400 text-slate-900 font-bold text-xs py-2"
                      size="sm"
                      onClick={() => robot.link && window.open(robot.link, '_blank')}
                    >
                      Assine Agora
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              {hoveredRobot !== robot.id && (
                <div className="flex items-center justify-between mt-auto">
                  <div className="flex-1">
                    {robot.monthlyPrice ? (
                      <div>
                        <div className="text-white font-bold text-base lg:text-lg">{robot.monthlyPrice}/mês</div>
                        <div className="text-slate-400 text-xs lg:text-sm">A partir de</div>
                      </div>
                    ) : (
                      <div className="text-white font-bold text-base lg:text-lg mb-1">{robot.price}</div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="bg-green-400/20 text-green-400 border-green-400/30 text-xs px-2 lg:px-3 py-1"
                    variant="outline"
                    onClick={() => robot.link && window.open(robot.link, '_blank')}
                  >
                    Assine Agora
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
    );
  };

  return (
    <section
      id="robots"
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden"
      onMouseEnter={() => setIsHoveringSection(true)}
      onMouseLeave={() => setIsHoveringSection(false)}
    >
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-green-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div
            ref={headerAnimation.ref}
            className={`pt-16 sm:pt-20 pb-12 sm:pb-16 text-center scroll-animate ${
              headerAnimation.isVisible ? 'visible' : ''
            }`}
          >
            <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-gradient-to-r from-green-400/20 via-cyan-400/20 to-purple-400/20 border border-green-400/30 mb-6 sm:mb-8 backdrop-blur-sm">
              <Bot className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 mr-2 sm:mr-3" />
              <span className="text-green-400 font-semibold text-sm sm:text-lg">Robôs de Trading Premium</span>
            </div>

            <h1 className="text-4xl sm:text-6xl lg:text-8xl font-black text-white mb-6 sm:mb-8 leading-tight">
              Estratégias
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-cyan-400 to-purple-400 animate-pulse">
                Automatizadas
              </span>
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-slate-300 max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-12 px-4">
              Escolha entre robôs individuais especializados ou combos exclusivos.
              <br />
              <span className="text-green-400 font-semibold">Performance comprovada em milhares de operações reais.</span>
            </p>

          <div 
            ref={statsAnimation.ref}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16 max-w-4xl mx-auto"
          >
            {[
              { icon: Shield, text1: "Validação", text2: "Profissional", color: "text-green-400" },
              { icon: Brain, text1: "Zero", text2: "Emoção", color: "text-cyan-400" },
              { icon: Zap, text1: "Mais", text2: "Liberdade", color: "text-purple-400" },
              { icon: TrendingUp, text1: "Gestão de", text2: "Risco", color: "text-yellow-400" }
            ].map((item, index) => (
              <div
                key={index}
                
                className={`bg-slate-800/50 backdrop-blur-sm border border-green-400/20 rounded-xl p-4 text-center scroll-animate stagger-${index + 1} ${
                  statsAnimation.isVisible ? 'visible' : ''
                }`}
              >
                <item.icon className={`mx-auto mb-3 h-8 w-8 ${item.color}`} />
                <div className={`text-sm font-bold ${item.color}`}>{item.text1}</div>
                <div className={`text-sm font-bold ${item.color}`}>{item.text2}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          {/* Navigation Buttons - Hide on mobile, show only on md+ */}
          <Button
            variant="ghost"
            className={getNavigationButtonClasses('left')}
            onClick={scrollPrev}
            onMouseEnter={handleNavigationMouseEnter}
            onMouseLeave={handleNavigationMouseLeave}
          >
            <ChevronLeft className="h-6 w-6 transition-all duration-300 ease-out group-hover:-translate-x-1 text-blue-100 group-hover:text-green-100" />
          </Button>

          <Button
            variant="ghost"
            className={getNavigationButtonClasses('right')}
            onClick={scrollNext}
            onMouseEnter={handleNavigationMouseEnter}
            onMouseLeave={handleNavigationMouseLeave}
          >
            <ChevronRight className="h-6 w-6 transition-all duration-300 ease-out group-hover:translate-x-1 text-blue-100 group-hover:text-green-100" />
          </Button>

          <div
            ref={(el) => {
              carouselRef.current = el;
              cardsAnimation.ref.current = el;
            }}
            className={`flex overflow-x-auto gap-4 sm:gap-6 pb-4 pl-4 pr-8 sm:px-0 scroll-animate ${
              cardsAnimation.isVisible ? 'visible' : ''
            }`}
            style={{
              scrollBehavior: 'smooth',
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              scrollPaddingLeft: '1rem',
              WebkitOverflowScrolling: 'touch',
              paddingRight: hoveredRobot ? '6rem' : '2rem'
            }}
          >
            {/* ~50 Cards para navegação extensa */}
            {/* 8 sequências completas = 48 cards */}
            {robots.map((robot) => renderRobotCard(robot, 'seq1-'))}
            {robots.map((robot) => renderRobotCard(robot, 'seq2-'))}
            {robots.map((robot) => renderRobotCard(robot, 'seq3-'))}
          </div>
        </div>

      </div>
    </section>
  );
});

RobotsSection.displayName = 'RobotsSection';

export default RobotsSection;
